import React from 'react';

import PrimaryButton from '@/components/ui/Button/PrimaryButton';
import RichText from '@/components/ui/RichText/RichText';

import type {
  CareServicesCard,
  CareServicesHomeVisit,
  CareServicesHomeVisitColumn,
  CareServicesSection,
} from '@/features/gpAccess/data/gpAccess.types';

const CheckIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="h-p-lg w-p-lg shrink-0"
  >
    <path
      d="M7.82812 0.318848C3.51483 0.318848 0 3.83368 0 8.14697C0 12.4603 3.51483 15.9751 7.82812 15.9751C12.1414 15.9751 15.6562 12.4603 15.6562 8.14697C15.6562 3.83368 12.1414 0.318848 7.82812 0.318848ZM11.57 6.3465L7.13142 10.7851C7.02183 10.8946 6.87309 10.9573 6.71653 10.9573C6.55997 10.9573 6.41123 10.8946 6.30164 10.7851L4.08628 8.56969C3.85927 8.34268 3.85927 7.96693 4.08628 7.73991C4.3133 7.51289 4.68905 7.51289 4.91606 7.73991L6.71653 9.54038L10.7402 5.51672C10.9672 5.28971 11.343 5.28971 11.57 5.51672C11.797 5.74374 11.797 6.11166 11.57 6.3465Z"
      fill="url(#pcp_check_grad)"
    />
    <defs>
      <linearGradient
        id="pcp_check_grad"
        x1="1"
        y1="6"
        x2="15"
        y2="8"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="var(--color-theme-information)" />
        <stop offset="1" stopColor="var(--color-theme-success-secondary)" />
      </linearGradient>
    </defs>
  </svg>
);

interface PrimaryCareServicesSectionProps {
  data?: CareServicesSection;
  onEnquireNow?: () => void;
}

const PrimaryCareServicesSection: React.FC<PrimaryCareServicesSectionProps> = ({
  data,
  onEnquireNow,
}) => {
  if (!data) return null;

  const cards = data.cards || [];
  const homeVisit = data.homeVisit;

  const cardsGridClassName =
    cards.length === 1
      ? 'grid grid-cols-1 gap-8'
      : cards.length === 2
        ? 'grid grid-cols-1 gap-8 md:grid-cols-2'
        : 'grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3';

  type TextContentStyles = {
    li?: string;
    p?: string;
  };

  const renderTextContentWithCheckList = (
    content: string,
    styles?: TextContentStyles,
  ) =>
    content
      .trim()
      .split('\n')
      .map((line, lineIndex) => {
        const trimmedLine = line.trim();

        if (!trimmedLine) return null;

        if (trimmedLine.startsWith('- ')) {
          return (
            <div
              key={`${trimmedLine}-${lineIndex}`}
              className="font-Muli-Regular text-theme-primary mb-4 flex items-start gap-3 text-[17px] leading-[28px]"
            >
              <CheckIcon />
              <span>{trimmedLine.replace('- ', '')}</span>
            </div>
          );
        }

        if (trimmedLine.startsWith('• ')) {
          return (
            <ul key={`${trimmedLine}-${lineIndex}`} className="ml-8 list-disc">
              <li
                className={
                  styles?.li ??
                  'font-Muli-Regular text-theme-primary text-[17px] leading-[28px]'
                }
              >
                {trimmedLine.replace('• ', '')}
              </li>
            </ul>
          );
        }

        return (
          <RichText
            key={`${trimmedLine}-${lineIndex}`}
            styles={{
              p:
                styles?.p ??
                'mb-3 text-[17px] font-Muli-Regular leading-[30px] text-[#4A4A4A]',
            }}
          >
            {trimmedLine}
          </RichText>
        );
      });

  const renderCard = (card: CareServicesCard, index: number) => (
    <div
      key={`${card.title?.content || 'primary-care-card'}-${index}`}
      className="flex min-h-[430px] w-full flex-col rounded-[6px] bg-white px-6 py-7 shadow-[0_16px_28px_rgba(0,0,0,0.12)]"
    >
      {card.title?.content && (
        <RichText
          styles={{
            h3: 'text-[25px] font-Muli-Bold leading-[32px] text-gray-900',
            p: 'text-[25px] font-Muli-Bold leading-[32px] text-gray-900',
            ...card.title.styles,
          }}
        >
          {card.title.content}
        </RichText>
      )}

      <div className="my-4 h-px w-full bg-gray-200" />

      {card.content?.content && (
        <div className="flex-1">
          {renderTextContentWithCheckList(
            card.content.content,
            card.content.styles,
          )}
        </div>
      )}

      {card.button?.content && (
        <PrimaryButton
          key={card.button.link || card.button.content || index}
          className={`tablet:w-auto tablet:text-lg mt-4 w-full text-center text-base ${
            card.button.styles?.className || ''
          }`}
          data={{
            content: card.button.content,
            title: card.button.content || '',
            link: card.button.link,
            functionality: (card.button as any).functionality,
            slug: (card.button as any).slug,
          }}
          onEnquireNow={onEnquireNow}
        />
      )}
    </div>
  );

  const renderHomeVisitColumn = (
    column: CareServicesHomeVisitColumn,
    index: number,
  ) => (
    <div key={`${column.title?.content || 'home-visit'}-${index}`}>
      {column.title?.content && (
        <div className="mb-3 flex items-center gap-3">
          <CheckIcon />

          <RichText
            styles={{
              h4: 'text-[17px] font-Muli-Bold leading-[26px] text-gray-900',
              p: 'text-[17px] font-Muli-Bold leading-[26px] text-gray-900',
              ...column.title.styles,
            }}
          >
            {column.title.content}
          </RichText>
        </div>
      )}

      {column.items && column.items.length > 0 && (
        <ul className="list-disc space-y-2 pl-7">
          {column.items.map((item, itemIndex) => (
            <li key={`${item.content}-${itemIndex}`}>
              <RichText
                styles={{
                  p: 'text-[17px] font-Muli-Regular leading-[26px] text-theme-primary',
                  ...item.styles,
                }}
              >
                {item.content}
              </RichText>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const renderHomeVisit = (homeVisitData: CareServicesHomeVisit) => (
    <div className="mt-8 rounded-[6px] bg-white px-6 py-7 shadow-[0_16px_28px_rgba(0,0,0,0.12)] md:px-7">
      {homeVisitData.title?.content && (
        <RichText
          styles={{
            h3: 'text-[25px] font-Muli-Bold leading-[32px] text-gray-900',
            p: 'text-[25px] font-Muli-Bold leading-[32px] text-gray-900',
            ...homeVisitData.title.styles,
          }}
        >
          {homeVisitData.title.content}
        </RichText>
      )}

      <div className="my-4 h-px w-full bg-gray-200" />

      {homeVisitData.columns && homeVisitData.columns.length > 0 && (
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {homeVisitData.columns.map(renderHomeVisitColumn)}
        </div>
      )}

      {homeVisitData.note?.content && (
        <div className="mt-8">
          <RichText
            styles={{
              p: 'text-[16px] font-Muli-Regular leading-[26px] text-[#4A4A4A]',
              ...homeVisitData.note.styles,
            }}
          >
            {homeVisitData.note.content}
          </RichText>
        </div>
      )}

      {homeVisitData.button?.content && (
        <div className="mt-7 flex justify-center">
          <PrimaryButton
            key={homeVisitData.button.link || homeVisitData.button.content}
            className={`tablet:w-auto tablet:text-lg mt-4 w-full text-center text-base ${
              homeVisitData.button.styles?.className || ''
            }`}
            data={{
              content: homeVisitData.button.content,
              title: homeVisitData.button.content || '',
              link: homeVisitData.button.link,
            }}
          />
        </div>
      )}
    </div>
  );

  return (
    <section
      className="w-full py-16 md:py-20"
      style={{ backgroundColor: data.backgroundColor || '#f4f4f4' }}
    >
      <div className="content_wrapper">
        <div className="mx-auto max-w-[1240px] px-4 lg:px-0">
          <div className="mb-8 text-center">
            {data.heading?.content && (
              <div className="mt-4 text-[#0F1437] [&_p]:mb-0">
                <RichText
                  styles={{
                    h2: 'text-[38px] font-Muli-Bold leading-[46px] md:text-[44px] md:leading-[54px]',
                    h3: 'text-[22px] font-bold leading-[30px] md:text-[24px] md:leading-[32px]',
                    p: 'text-[22px] font-bold leading-[30px] md:text-[24px] md:leading-[32px]',
                    ...data.heading.styles,
                  }}
                >
                  {data.heading.content}
                </RichText>
              </div>
            )}

            {data.description?.content && (
              <div className="mt-4 text-[#0F1437] [&_p]:mb-0">
                <RichText
                  styles={{
                    p: 'text-[16px] font-Muli-Regular leading-[24px] text-[#4A4A4A]',
                    ...data.description.styles,
                  }}
                >
                  {data.description.content}
                </RichText>
              </div>
            )}
          </div>

          {cards.length > 0 && (
            <div className={cardsGridClassName}>{cards.map(renderCard)}</div>
          )}

          {homeVisit && renderHomeVisit(homeVisit)}
        </div>
      </div>
    </section>
  );
};

export default PrimaryCareServicesSection;
